import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  useDisclosure, Flex, Text, Box, HStack, Input, Tooltip, IconButton, Select,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { FiEdit2, FiTrash2, FiUsers, FiCheckSquare, FiCamera, FiUnlock } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioTaller from './FormularioTaller';
import ModalInscripciones from './ModalInscripciones';
import ModalAsistencia from './ModalAsistencia';
import ModalEvidenciasTaller from './ModalEvidenciasTaller';

const TIPOS = ['Todos', 'Arte', 'Música', 'Danza', 'Teatro', 'Literatura', 'Artesanías', 'Otro'];
const ESTATUSES = ['Todos', 'Borrador', 'Abierto', 'Concluido'];

const colorEstatus: Record<string, string> = {
  Borrador: 'gray',
  Abierto: 'green',
  Concluido: 'blue',
};

const TablaTalleresCulturales: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();
  const { isOpen: isOpenInscripciones, onOpen: onOpenInscripciones, onClose: onCloseInscripciones } = useDisclosure();
  const { isOpen: isOpenAsistencia, onOpen: onOpenAsistencia, onClose: onCloseAsistencia } = useDisclosure();
  const { isOpen: isOpenEvidencias, onOpen: onOpenEvidencias, onClose: onCloseEvidencias } = useDisclosure();

  const [data, setData] = useState<any[]>([]);
  const [filtroTipo, setFiltroTipo] = useState('Todos');
  const [filtroEstatus, setFiltroEstatus] = useState('Todos');
  const [busqueda, setBusqueda] = useState('');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [eliminando, setEliminando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data: res } = await axiosInstance.get('/cultura/talleres');
      setData(res);
    } catch {
      toast({ title: 'Error al cargar talleres', status: 'error', duration: 3000 });
    }
  }, [toast]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarDetalle = async (taller: any) => {
    try {
      const { data: res } = await axiosInstance.get(`/cultura/talleres/${taller.id}`);
      return res;
    } catch {
      return taller;
    }
  };

  const filtrados = data.filter((t) => {
    const matchTipo = filtroTipo === 'Todos' || t.tipo === filtroTipo;
    const matchEstatus = filtroEstatus === 'Todos' || t.estatus === filtroEstatus;
    const matchBusqueda = !busqueda ||
      t.titulo.toLowerCase().includes(busqueda.toLowerCase()) ||
      t.tallerista.toLowerCase().includes(busqueda.toLowerCase());
    return matchTipo && matchEstatus && matchBusqueda;
  });

  const handleEliminar = async () => {
    if (!selectedItem) return;
    setEliminando(true);
    try {
      await axiosInstance.delete(`/cultura/talleres/${selectedItem.id}`);
      toast({ title: 'Taller eliminado', status: 'success', duration: 3000 });
      onCloseEliminar();
      cargar();
    } catch (e: any) {
      toast({ title: 'Error al eliminar', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setEliminando(false);
    }
  };

  const handleAbrirInscripciones = async (taller: any) => {
    try {
      await axiosInstance.put(`/cultura/talleres/${taller.id}/abrir`);
      toast({ title: 'Inscripciones abiertas', status: 'success', duration: 2000 });
      cargar();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    }
  };

  const abrirConDetalle = async (taller: any, abrirModal: () => void) => {
    const detalle = await cargarDetalle(taller);
    setSelectedItem(detalle);
    abrirModal();
  };

  const formatFecha = (f: string) => {
    if (!f) return '-';
    return new Date(f).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
      {/* Filtros */}
      <Flex mb={4} gap={3} flexWrap="wrap" align="center" justify="space-between">
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por título o tallerista..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="sm"
            w={{ base: 'full', md: '220px' }}
          />
          <Select size="sm" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} w="140px">
            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Select size="sm" value={filtroEstatus} onChange={(e) => setFiltroEstatus(e.target.value)} w="130px">
            {ESTATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }}
          onClick={onOpenNuevo}>
          Nuevo taller
        </Button>
      </Flex>

      {/* Tabla */}
      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Título</Th>
              <Th>Tipo</Th>
              <Th>Tallerista</Th>
              <Th>Lugar</Th>
              <Th>Fecha</Th>
              <Th isNumeric>Cupo</Th>
              <Th isNumeric>Inscritos</Th>
              <Th>Estatus</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtrados.length === 0 ? (
              <Tr><Td colSpan={9} textAlign="center" color="gray.400" py={6}>Sin talleres registrados</Td></Tr>
            ) : filtrados.map((t) => (
              <Tr key={t.id} _hover={{ bg: 'gray.50' }}>
                <Td fontWeight="medium" maxW="200px">
                  <Text noOfLines={1}>{t.titulo}</Text>
                </Td>
                <Td>{t.tipo}</Td>
                <Td>{t.tallerista}</Td>
                <Td maxW="140px"><Text noOfLines={1}>{t.lugar}</Text></Td>
                <Td>{formatFecha(t.fechaTaller)}</Td>
                <Td isNumeric>{t.cupo}</Td>
                <Td isNumeric>{t._count?.inscripciones ?? 0}</Td>
                <Td>
                  <Badge colorScheme={colorEstatus[t.estatus] || 'gray'}>{t.estatus}</Badge>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <Tooltip label="Editar">
                      <IconButton aria-label="Editar" icon={<FiEdit2 />} size="xs" variant="ghost"
                        onClick={() => { setSelectedItem(t); onOpenEditar(); }} />
                    </Tooltip>
                    {t.estatus === 'Borrador' && (
                      <Tooltip label="Abrir inscripciones">
                        <IconButton aria-label="Abrir" icon={<FiUnlock />} size="xs" variant="ghost" colorScheme="green"
                          onClick={() => handleAbrirInscripciones(t)} />
                      </Tooltip>
                    )}
                    {t.estatus === 'Abierto' && (
                      <Tooltip label="Inscripciones">
                        <IconButton aria-label="Inscripciones" icon={<FiUsers />} size="xs" variant="ghost" colorScheme="blue"
                          onClick={() => abrirConDetalle(t, onOpenInscripciones)} />
                      </Tooltip>
                    )}
                    {t.estatus !== 'Borrador' && (
                      <Tooltip label="Asistencia y cierre">
                        <IconButton aria-label="Asistencia" icon={<FiCheckSquare />} size="xs" variant="ghost" colorScheme="purple"
                          onClick={() => abrirConDetalle(t, onOpenAsistencia)} />
                      </Tooltip>
                    )}
                    <Tooltip label="Evidencias">
                      <IconButton aria-label="Evidencias" icon={<FiCamera />} size="xs" variant="ghost" colorScheme="teal"
                        onClick={() => abrirConDetalle(t, onOpenEvidencias)} />
                    </Tooltip>
                    <Tooltip label="Eliminar">
                      <IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                        onClick={() => { setSelectedItem(t); onOpenEliminar(); }} />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Modal Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Nuevo Taller</ModalHeader>
          <ModalBody py={4}>
            <FormularioTaller onGuardado={() => { onCloseNuevo(); cargar(); }} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Editar Taller</ModalHeader>
          <ModalBody py={4}>
            <FormularioTaller taller={selectedItem} onGuardado={() => { onCloseEditar(); cargar(); }} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal Inscripciones */}
      {selectedItem && isOpenInscripciones && (
        <ModalInscripciones
          isOpen={isOpenInscripciones}
          onClose={onCloseInscripciones}
          taller={selectedItem}
          onGuardado={cargar}
        />
      )}

      {/* Modal Asistencia */}
      {selectedItem && isOpenAsistencia && (
        <ModalAsistencia
          isOpen={isOpenAsistencia}
          onClose={onCloseAsistencia}
          taller={selectedItem}
          onGuardado={cargar}
        />
      )}

      {/* Modal Evidencias */}
      {selectedItem && isOpenEvidencias && (
        <ModalEvidenciasTaller
          isOpen={isOpenEvidencias}
          onClose={onCloseEvidencias}
          taller={selectedItem}
          onGuardado={cargar}
        />
      )}

      {/* Confirmar eliminar */}
      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar taller</AlertDialogHeader>
            <AlertDialogBody>
              ¿Eliminar <strong>{selectedItem?.titulo}</strong>? Se eliminarán todos los datos relacionados.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminar} ml={3} isLoading={eliminando} loadingText="Eliminando...">
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaTalleresCulturales;
